import { useMemo, useRef, useState } from 'react';
import MuxVideo from '@mux/mux-video-react';

import { cn } from '@blockworks/ui/utils';

import PauseIcon from './icons/pause-icon';
import PlayIcon from './icons/play-icon';

const MuxVideoAdPlayer = ({
    playbackId,
    videoTitle,
    linkTo,
}: {
    playbackId: string;
    videoTitle: string;
    linkTo: string;
}) => {
    const videoRef = useRef<HTMLVideoElement>(null);
    const [isPaused, setIsPaused] = useState(false);
    const [isHovered, setIsHovered] = useState(false);
    const [isEnded, setIsEnded] = useState(false);

    const handleButtonClick = () => {
        if (isPaused) {
            setIsEnded(false);
            setIsPaused(false);
            videoRef.current?.play();
        } else {
            setIsPaused(true);
            videoRef.current?.pause();
        }
    };

    const memoizedIsBackgroundBlur = useMemo(() => isEnded || isHovered, [isEnded, isHovered]);

    return (
        <div
            className="w-full h-full relative"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            aria-hidden="true"
        >
            <div
                className={cn('absolute inset-0 bg-black transition-opacity duration-300 opacity-0', {
                    'opacity-50': memoizedIsBackgroundBlur,
                })}
            />

            <MuxVideo
                ref={videoRef}
                className="w-full h-full object-cover"
                playbackId={playbackId}
                metadata={{ video_title: videoTitle }}
                autoPlay
                onPlay={() => setIsPaused(false)}
                muted
                streamType="on-demand"
                onEnded={() => {
                    setIsPaused(true);
                    setIsEnded(true);
                }}
            />

            <div
                className={cn(
                    `flex flex-col items-center justify-center absolute left-1/2 top-[56%] -translate-x-1/2 -translate-y-1/2 z-50 transition-opacity duration-300 opacity-0`,
                    {
                        'opacity-100': memoizedIsBackgroundBlur,
                    },
                )}
            >
                <button
                    onClick={handleButtonClick}
                    aria-label={isPaused ? 'Play video' : 'Pause video'}
                    className="bg-transparent border-none cursor-pointer"
                >
                    {isPaused ? (
                        <PlayIcon className="fill-white/80 hover:fill-white h-16 w-16 md:h-14 md:w-14 lg:h-12 lg:w-12" />
                    ) : (
                        <PauseIcon className="fill-white/80 hover:fill-white h-16 w-16 md:h-14 md:w-14 lg:h-12 lg:w-12" />
                    )}
                </button>
                <a
                    href={linkTo}
                    target="_blank"
                    rel="sponsored nofollow"
                    className="block mt-3 lg:mt-1.5 uppercase text-white bg-brand hover:bg-gray-700 px-3 py-2 lg:px-2 lg:py-1 text-md lg:text-xs transition duration-300"
                >
                    learn more
                </a>
            </div>
        </div>
    );
};

export default MuxVideoAdPlayer;
